/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { PluginInternalState } from '@opengeoweb/plugin-interface';
import type { ConfigType } from '@opengeoweb/shared';
import { PluginComponent } from './PluginComponent';
import { pluginsActions } from '../store';

interface PluginComponentProps {
  sourceId: string;
  pluginId: string;
  pluginState?: PluginInternalState;
  version?: string;
  applicationConfig?: ConfigType;
  hostPanelId?: string;
}
export const PluginComponentConnect: React.FC<PluginComponentProps> = ({
  sourceId,
  pluginId,
  pluginState,
  version,
  applicationConfig,
  hostPanelId,
}): React.ReactElement => {
  const dispatch = useDispatch();

  const onGeoWebPluginStateChange = (state: PluginInternalState): void => {
    if (!isEqual(state, pluginState)) {
      dispatch(
        pluginsActions.setGeoWebPluginState({
          panelId: sourceId,
          pluginState: state,
        }),
      );
    }
  };

  useEffect(() => {
    return (): void => {
      dispatch(pluginsActions.clearGeoWebPluginState({ panelId: sourceId }));
    };
  }, [dispatch, sourceId]);

  return (
    <PluginComponent
      applicationConfig={applicationConfig}
      hostPanelId={hostPanelId}
      onGeoWebPluginStateChange={onGeoWebPluginStateChange}
      pluginId={pluginId}
      pluginState={pluginState}
      sourceId={sourceId}
      version={version}
    />
  );
};
